import type * as THREE from 'three';
import { AppConfig } from '../config';
import { LatencyEstimator } from './LatencyEstimator';
import { PoseHistory, unprojectThroughSnapshot, type CameraPoseSnapshot } from './PoseHistory';
import type { NormalizedPoint } from '@questvisionstream/client';

/**
 * The app-wide capture-pose tracker - the web home of Unity's
 * `IPoseTrackingService`. One `PoseHistory` (recorded every frame by the
 * detection render system) and one `LatencyEstimator` (fed by every
 * detections payload), shared by every consumer that places geometry through
 * the capture-time pose: detection boxes, assistant strokes, the training
 * world label. Sharing them is what makes a stroke land on the same frame
 * pose as the box it was drawn over.
 */
class PoseTracking {
  readonly history = new PoseHistory();
  readonly latency = new LatencyEstimator(AppConfig.assumedLatencyMs);

  /** Record the camera for this frame. */
  record(camera: THREE.Camera): void {
    this.history.record(performance.now(), camera);
  }

  /** The latency-compensated capture snapshot for a payload that arrived at `arrivalMs`. */
  snapshotForArrival(arrivalMs: number): CameraPoseSnapshot | null {
    return this.history.lookup(arrivalMs - this.latency.latencyMs());
  }

  /** Unproject a normalized viewport point through the capture pose for `arrivalMs`; null without history. */
  unprojectAtArrival(point: NormalizedPoint, arrivalMs: number, distanceM: number): THREE.Vector3 | null {
    const snapshot = this.snapshotForArrival(arrivalMs);
    return snapshot ? unprojectThroughSnapshot(snapshot, point, distanceM) : null;
  }

  clear(): void {
    this.history.clear();
  }
}

export const poseTracking = new PoseTracking();
